import { Button } from "@/components/ui/button";
import { Trash2, Download, ChevronDown, ChevronUp, Loader2, AlertTriangle } from "lucide-react";

function CaseDetail({ detail, onDownload }) {
  if (!detail) return null;
  const fd = detail.final?.final_diagnosis || {};
  const phar = detail.final?.treatment?.pharmacological || [];
  const refs = detail.final?.references || [];
  return (
    <div className="pt-5">
      <p className="text-xs font-semibold uppercase tracking-widest text-[#002FA7]">Final diagnosis</p>
      <h3 className="font-serif text-xl text-zinc-900 mt-1">{fd.condition}</h3>
      <p className="text-sm text-zinc-700 mt-2">{fd.justification}</p>

      {phar.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-zinc-900 mb-1.5">Treatment</p>
          <ul className="space-y-1">
            {phar.map((d, i) => (
              <li key={d.drug || i} className="text-sm text-zinc-700">• {d.drug} — {d.dose}{d.duration ? ` · ${d.duration}` : ""}</li>
            ))}
          </ul>
        </div>
      )}

      {refs.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-zinc-900 mb-1.5">References</p>
          <ul className="space-y-1">
            {refs.map((r, i) => (
              <li key={r.citation || r.source || i} className="text-xs text-zinc-600 border-l-2 border-[#002FA7] pl-2"><span className="font-semibold">{r.source}:</span> {r.citation}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5">
        <Button onClick={onDownload} data-testid={`case-pdf-${detail.id}`} variant="outline" size="sm" className="border-[#002FA7] text-[#002FA7] hover:bg-blue-50">
          <Download className="h-4 w-4 mr-2" /> Download report (PDF)
        </Button>
      </div>
    </div>
  );
}

export default function CaseRow({ caseItem, open, detail, loadingDetail, onToggle, onRemove, onDownload }) {
  const c = caseItem;
  return (
    <div className="bg-white" data-testid={`case-${c.id}`}>
      <div className="p-5 flex items-center justify-between gap-4">
        <button onClick={() => onToggle(c.id)} className="flex-1 text-left" data-testid={`case-toggle-${c.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-zinc-900">{c.condition || c.title || "Case"}</p>
            {c.red_flag && (
              <span data-testid={`case-redflag-${c.id}`} className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                <AlertTriangle className="h-3 w-3" /> Red flag
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-400 mt-0.5">
            {c.title} · {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
          </p>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => onRemove(c.id)} data-testid={`case-delete-${c.id}`} className="p-2 text-zinc-400 hover:text-red-600 transition-colors">
            <Trash2 className="h-4 w-4" />
          </button>
          <button onClick={() => onToggle(c.id)} className="p-2 text-zinc-400 hover:text-zinc-700 transition-colors">
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="px-5 pb-6 border-t border-zinc-100" data-testid={`case-detail-${c.id}`}>
          {loadingDetail && <div className="flex items-center gap-2 text-zinc-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
          <CaseDetail detail={detail} onDownload={() => onDownload(detail)} />
        </div>
      )}
    </div>
  );
}
